import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { toastHelpers } from '@/lib/toast-helpers'
import { ORDERING_STATUS_QUERY_KEY } from '@/components/admin/online-orders/OrderingStatusControl'

// F-ONL-N4: the unanswered-order window. Stored as a whole number of minutes
// under this key; 0 is off. The backend clamps and validates (5–120).
export const UNANSWERED_EXPIRE_KEY = 'online.unanswered_expire_minutes'
const MINUTE_CHOICES = [5, 10, 15, 20, 30, 45, 60]
const DEFAULT_MINUTES = 15

/**
 * Admin / manager only (the settings route is). Off by default on every
 * restaurant: closing a customer's order is a decision the restaurant makes,
 * not something a deploy switches on.
 */
export function OnlineOrderSettingsDialog({
  open,
  onOpenChange,
  currentMinutes,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentMinutes: number
}) {
  const queryClient = useQueryClient()
  const [enabled, setEnabled] = useState(currentMinutes > 0)
  const [minutes, setMinutes] = useState(currentMinutes > 0 ? currentMinutes : DEFAULT_MINUTES)

  useEffect(() => {
    if (open) {
      setEnabled(currentMinutes > 0)
      setMinutes(currentMinutes > 0 ? currentMinutes : DEFAULT_MINUTES)
    }
  }, [open, currentMinutes])

  const save = useMutation({
    mutationFn: () => apiClient.updateSetting(UNANSWERED_EXPIRE_KEY, enabled ? minutes : 0),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ORDERING_STATUS_QUERY_KEY })
      // Incoming cards show their countdown from expires_at.
      queryClient.invalidateQueries({ queryKey: ['online-orders', 'pending'] })
      queryClient.invalidateQueries({ queryKey: ['settings', 'all'] })
      toastHelpers.success(
        'Saved',
        enabled ? `Orders nobody answers are cancelled after ${minutes} min.` : 'Unanswered orders wait until someone answers them.',
      )
      onOpenChange(false)
    },
    onError: (err: unknown) => {
      const e = err as { message?: string }
      toastHelpers.error('Could not save', e?.message || 'Please try again.')
    },
  })

  const choices = MINUTE_CHOICES.includes(minutes) ? MINUTE_CHOICES : [...MINUTE_CHOICES, minutes].sort((a, b) => a - b)

  return (
    <Dialog open={open} onOpenChange={(o) => !save.isPending && onOpenChange(o)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Online order settings</DialogTitle>
          <DialogDescription>Applies to every screen showing online orders.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 rounded-lg border border-border p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <Label htmlFor="unanswered-expire" className="text-sm font-medium">
                Cancel orders nobody answers
              </Label>
              <p className="mt-1 text-xs text-muted-foreground">
                If no one accepts or rejects a new order in time, it is cancelled and the customer is told the
                restaurant didn&rsquo;t confirm it. Nothing is made and nothing is charged.
              </p>
            </div>
            <Switch id="unanswered-expire" checked={enabled} onCheckedChange={setEnabled} />
          </div>
          {enabled && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">Cancel after</span>
              <Select value={String(minutes)} onValueChange={(v) => setMinutes(Number(v))}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {choices.map((m) => (
                    <SelectItem key={m} value={String(m)}>
                      {m} minutes
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={save.isPending}>
            Close
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
